import * as React from 'react';
import ReactMarkdown from 'react-markdown';
import { renderToStaticMarkup } from 'react-dom/server';
import remarkGfm from 'remark-gfm';
import { getIdpBaseUrl } from '@circuitry-ai/doc-data';
import type { ReleaseNotesEntry } from '@circuitry-ai/doc-data';

/**
 * Release-notes downloads — mirrors dcc-web's `notesDownload.ts`.
 *
 * Word: fetches the IDP service's `notes/export.docx` endpoint (server renders
 * markdown -> .docx) as an authenticated blob and saves it.
 *
 * PDF: printed client-side. We render the notes markdown to HTML and print it
 * from a hidden iframe — the user picks "Save as PDF". This keeps heavy PDF
 * libraries out of the bundle and the backend.
 */

function escapeHtml(text: string): string {
  return text.replace(/[&<>"']/g, (char) => {
    switch (char) {
      case '&':
        return '&amp;';
      case '<':
        return '&lt;';
      case '>':
        return '&gt;';
      case '"':
        return '&quot;';
      default:
        return '&#39;';
    }
  });
}

/** Download the release notes as a Word (.docx) file. */
export async function downloadReleaseNotesWord(
  release: ReleaseNotesEntry,
  token?: string,
  customer?: string,
  baseUrl?: string
): Promise<void> {
  const base = (baseUrl ?? getIdpBaseUrl()).trim().replace(/\/+$/, '');
  if (!base) {
    throw new Error('IDP base URL is not configured. Set CTY_IDP_API_URL or call setIdpConfig({ baseUrl }).');
  }

  const qs = customer ? `?customer=${encodeURIComponent(customer)}` : '';
  const response = await fetch(`${base}/api/v1/releases/${release.id}/notes/export.docx${qs}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!response.ok) {
    throw new Error(`Export failed (${response.status})`);
  }

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `ReleaseNotes_${release.name}.docx`;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

/**
 * Print the release notes to PDF via a hidden iframe (no pop-up window, so it
 * is not blocked). Styling mirrors the Circuitry Release Notes template (navy
 * headings, a shaded metadata table, a footer rule) so the printed PDF matches
 * the .docx export's look.
 */
export function printReleaseNotesPdf(release: ReleaseNotesEntry): void {
  const bodyHtml = renderToStaticMarkup(
    React.createElement(ReactMarkdown as React.ElementType, { remarkPlugins: [remarkGfm] }, release.markdown) as any
  );
  const title = `Release Notes — ${release.name}`;

  const iframe = document.createElement('iframe');
  iframe.setAttribute('aria-hidden', 'true');
  iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
  document.body.appendChild(iframe);
  const doc = iframe.contentWindow?.document;
  if (!doc) {
    iframe.remove();
    return;
  }
  doc.open();
  doc.write(
    `<!doctype html><html><head><meta charset="utf-8"><title>${escapeHtml(title)}</title><style>` +
      'body{font-family:Calibri,ui-sans-serif,system-ui,-apple-system,Segoe UI,Roboto,sans-serif;color:#111;' +
      'max-width:7in;margin:0 auto;padding:40px;line-height:1.5;}' +
      'h1{color:#1F3864;font-size:26px;font-weight:700;margin:0 0 14px;}' +
      'h2{color:#1F3864;font-size:17px;font-weight:700;margin:22px 0 8px;}' +
      'h3{font-size:14px;font-weight:700;margin:16px 0 6px;}' +
      'p{margin:8px 0;}' +
      'table{border-collapse:collapse;width:100%;max-width:6.5in;margin:10px 0 16px;}' +
      'td,th{border-top:1px solid #D9D9D9;border-bottom:1px solid #D9D9D9;padding:5px 10px;' +
      'text-align:left;font-size:13px;vertical-align:top;}' +
      'table td:first-child{background:#F2F2F2;font-weight:700;width:2in;}' +
      'ul{padding-left:22px;margin:6px 0;}li{margin:3px 0;}' +
      'strong{font-weight:700;}em{color:#595959;font-style:italic;font-size:12px;}' +
      'hr{border:none;border-top:1px solid #D9D9D9;margin:18px 0;}' +
      `</style></head><body>${bodyHtml}` +
      '<script>window.onload=function(){window.focus();window.print();}</scri' +
      'pt>' +
      '</body></html>'
  );
  doc.close();
  // Remove the iframe after the print dialog has had time to open.
  window.setTimeout(() => iframe.remove(), 60000);
}
